import React from 'react';
import { 
  Phone, Mail, MapPin, Clock, Star, ChevronRight, 
  MessageCircle, Calendar, Menu, X, Check, ArrowRight,
  Zap, Wrench, Lightbulb, LayoutGrid, FileCheck, Home as HomeIcon
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Icon mapping for services
const iconMap = {
  zap: Zap,
  wrench: Wrench,
  lightbulb: Lightbulb,
  'layout-grid': LayoutGrid,
  'file-check': FileCheck,
  home: HomeIcon
};

const TemplateMadrid = ({ business, isPreview = false, isMobile = false }) => {
  const [menuOpen, setMenuOpen] = React.useState(false);
  const [showAllServices, setShowAllServices] = React.useState(false);
  const [showAllGallery, setShowAllGallery] = React.useState(false);

  if (!business) return null;

  const {
    name,
    category,
    city,
    province,
    phone,
    whatsapp,
    email,
    logo,
    headline,
    description,
    services = [],
    gallery = [],
    testimonials = [],
    service_areas = [],
    business_hours,
    work_process = [],
    design_settings = {}
  } = business;

  const primaryColor = design_settings.primary_color || '#1e40af';
  const secondaryColor = design_settings.secondary_color || '#3b82f6';
  const heroImage = design_settings.hero_image || 'https://images.unsplash.com/photo-1621905252507-b35492cc74b4?w=1200';

  // Services to show (4 on mobile initially)
  const visibleServices = showAllServices ? services : services.slice(0, 4);
  const visibleGallery = showAllGallery ? gallery : gallery.slice(0, 3);

  const handleWhatsApp = (message = '') => {
    const defaultMsg = message || `Hola, he visto tu página y me gustaría solicitar información sobre tus servicios.`;
    const phoneNum = (whatsapp || phone)?.replace(/\D/g, '');
    window.open(`https://wa.me/${phoneNum}?text=${encodeURIComponent(defaultMsg)}`, '_blank');
  };

  const handlePresupuesto = () => {
    // Por enquanto, scroll para seção de contato ou abre modal
    document.getElementById('cta-section')?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleCita = () => {
    // Por enquanto, abre WhatsApp com mensagem específica
    handleWhatsApp('Hola, me gustaría agendar una cita para un servicio.');
  };

  const getCategoryLabel = (cat) => {
    const labels = {
      electricista: 'Electricista',
      fontanero: 'Fontanero',
      reformas: 'Reformas',
      limpieza: 'Limpieza',
      jardineria: 'Jardinería'
    };
    return labels[cat] || cat;
  };

  return (
    <div className="min-h-screen bg-white font-['Inter',sans-serif]">
      {/* ========== HEADER ========== */}
      <header className="sticky top-0 z-50 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
          {/* Logo/Name */}
          <div className="flex items-center gap-3">
            {logo ? (
              <img src={logo} alt={name} className="h-10 w-auto" />
            ) : (
              <div 
                className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-lg"
                style={{ backgroundColor: primaryColor }}
              >
                {name?.charAt(0)}
              </div>
            )}
            <span className="font-semibold text-slate-900 hidden sm:block">{name}</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden md:flex items-center gap-6 text-sm">
            <a href="#servicios" className="text-slate-600 hover:text-slate-900 transition-colors">Servicios</a>
            <a href="#trabajos" className="text-slate-600 hover:text-slate-900 transition-colors">Trabajos</a>
            <a href="#opiniones" className="text-slate-600 hover:text-slate-900 transition-colors">Opiniones</a>
            <a href="#contacto" className="text-slate-600 hover:text-slate-900 transition-colors">Contacto</a>
          </nav>

          {/* CTA Desktop */}
          <div className="hidden md:flex items-center gap-3">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => handleWhatsApp()}
              className="border-green-500 text-green-600 hover:bg-green-50"
            >
              <MessageCircle size={16} className="mr-2" />
              WhatsApp
            </Button>
            <Button 
              size="sm"
              onClick={handlePresupuesto}
              style={{ backgroundColor: primaryColor }}
              className="hover:opacity-90"
            >
              Solicitar presupuesto
            </Button>
          </div>

          {/* Mobile Menu Button */}
          <button 
            className="md:hidden p-2"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            {menuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden bg-white border-t border-slate-100 py-4 px-4">
            <nav className="flex flex-col gap-3">
              <a href="#servicios" className="text-slate-700 py-2" onClick={() => setMenuOpen(false)}>Servicios</a>
              <a href="#trabajos" className="text-slate-700 py-2" onClick={() => setMenuOpen(false)}>Trabajos</a>
              <a href="#opiniones" className="text-slate-700 py-2" onClick={() => setMenuOpen(false)}>Opiniones</a>
              <a href="#contacto" className="text-slate-700 py-2" onClick={() => setMenuOpen(false)}>Contacto</a>
            </nav>
          </div>
        )}
      </header>

      {/* ========== HERO ========== */}
      <section className="relative">
        {/* Background Image */}
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: `url(${heroImage})` }}
        >
          <div className="absolute inset-0 bg-gradient-to-r from-slate-900/90 via-slate-900/70 to-slate-900/50" />
        </div>

        <div className="relative max-w-6xl mx-auto px-4 py-16 sm:py-24 lg:py-32">
          <div className="max-w-2xl">
            {/* Category Badge */}
            <div 
              className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-sm mb-4"
              style={{ backgroundColor: `${primaryColor}30`, color: '#fff' }}
            >
              <MapPin size={14} />
              <span>{getCategoryLabel(category)} en {city} {province && `y ${province}`}</span>
            </div>

            {/* Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
              {headline}
            </h1>

            {/* Description */}
            {description && (
              <p className="text-lg text-slate-300 mb-8 leading-relaxed">
                {description}
              </p>
            )}

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                size="lg"
                onClick={handlePresupuesto}
                className="text-base px-8 py-6 hover:opacity-90"
                style={{ backgroundColor: primaryColor }}
              >
                Solicitar presupuesto
                <ArrowRight size={18} className="ml-2" />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => handleWhatsApp()}
                className="text-base px-8 py-6 border-white/30 text-white hover:bg-white/10 bg-white/5"
              >
                <MessageCircle size={18} className="mr-2" />
                WhatsApp
              </Button>
            </div>

            {/* Trust indicators */}
            <div className="flex items-center gap-6 mt-8 pt-8 border-t border-white/20">
              <div className="flex items-center gap-2">
                <div className="flex">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={16} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-white/80 text-sm">5.0 valoración</span>
              </div>
              <div className="flex items-center gap-2 text-white/80 text-sm">
                <Check size={16} className="text-green-400" />
                <span>Respuesta rápida</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SERVICIOS ========== */}
      <section id="servicios" className="py-16 sm:py-20 bg-slate-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
              ¿Qué necesitas?
            </h2>
            <p className="text-slate-600">Servicios profesionales con garantía de calidad</p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {visibleServices.map((service) => {
              const IconComponent = iconMap[service.icon] || Zap;
              return (
                <div 
                  key={service.id}
                  className="bg-white rounded-xl p-6 border border-slate-200 hover:border-blue-200 hover:shadow-lg transition-all group cursor-pointer"
                >
                  <div 
                    className="w-12 h-12 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                    style={{ backgroundColor: `${primaryColor}15` }}
                  >
                    <IconComponent size={24} style={{ color: primaryColor }} />
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{service.name}</h3>
                  {service.description && (
                    <p className="text-sm text-slate-600 mb-3">{service.description}</p>
                  )}
                  {service.price && (
                    <span className="text-sm font-medium" style={{ color: primaryColor }}>
                      {service.price}
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {services.length > 4 && !showAllServices && (
            <div className="text-center mt-8">
              <Button 
                variant="outline"
                onClick={() => setShowAllServices(true)}
                className="border-slate-300"
              >
                Ver todos los servicios
                <ChevronRight size={16} className="ml-1" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* ========== TRABAJOS REALIZADOS ========== */}
      {gallery.length > 0 && (
        <section id="trabajos" className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Trabajos realizados
              </h2>
              <p className="text-slate-600">Proyectos completados con éxito</p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleGallery.map((item) => (
                <div 
                  key={item.id}
                  className="group relative rounded-xl overflow-hidden aspect-[4/3] cursor-pointer"
                >
                  <img 
                    src={item.image_url} 
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-4">
                      <h4 className="text-white font-medium">{item.title}</h4>
                      {item.description && (
                        <p className="text-white/80 text-sm">{item.description}</p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {gallery.length > 3 && !showAllGallery && (
              <div className="text-center mt-8">
                <Button 
                  variant="outline"
                  onClick={() => setShowAllGallery(true)}
                  className="border-slate-300"
                >
                  Ver más trabajos
                  <ChevronRight size={16} className="ml-1" />
                </Button>
              </div>
            )}
          </div>
        </section>
      )}

      {/* ========== CÓMO TRABAJAMOS ========== */}
      {work_process.length > 0 && (
        <section className="py-16 sm:py-20 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Cómo trabajamos
              </h2>
              <p className="text-slate-600">Proceso simple y transparente</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {work_process.map((step, index) => (
                <div key={index} className="text-center">
                  <div 
                    className="w-16 h-16 rounded-full flex items-center justify-center text-white text-2xl font-bold mx-auto mb-4"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {step.step}
                  </div>
                  <h3 className="font-semibold text-slate-900 mb-2">{step.title}</h3>
                  <p className="text-slate-600 text-sm">{step.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== OPINIONES ========== */}
      {testimonials.length > 0 && (
        <section id="opiniones" className="py-16 sm:py-20">
          <div className="max-w-6xl mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-2xl sm:text-3xl font-bold text-slate-900 mb-3">
                Opiniones de clientes
              </h2>
              <div className="flex items-center justify-center gap-2">
                <div className="flex">
                  {[1,2,3,4,5].map(i => (
                    <Star key={i} size={20} className="text-yellow-400 fill-yellow-400" />
                  ))}
                </div>
                <span className="text-slate-600">5.0 de valoración media</span>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {testimonials.map((testimonial) => (
                <div 
                  key={testimonial.id}
                  className="bg-white rounded-xl p-6 border border-slate-200"
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium">
                      {testimonial.name?.charAt(0)}
                    </div>
                    <div>
                      <h4 className="font-medium text-slate-900">{testimonial.name}</h4>
                      {testimonial.service && (
                        <p className="text-sm text-slate-500">{testimonial.service}</p>
                      )}
                    </div>
                  </div>
                  <div className="flex mb-3">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} size={14} className="text-yellow-400 fill-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 text-sm leading-relaxed">"{testimonial.text}"</p>
                  {testimonial.is_demo && (
                    <p className="text-xs text-slate-400 mt-3 italic">Ejemplo demostrativo</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== ZONA DE SERVICIO ========== */}
      {service_areas.length > 0 && (
        <section className="py-12 bg-slate-50">
          <div className="max-w-6xl mx-auto px-4 text-center">
            <h3 className="font-semibold text-slate-900 mb-3">Zona de servicio</h3>
            <div className="flex flex-wrap justify-center gap-2">
              {service_areas.map((area, idx) => (
                <span 
                  key={idx}
                  className="px-3 py-1 bg-white rounded-full text-sm text-slate-700 border border-slate-200"
                >
                  {area}
                </span>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ========== CTA FINAL ========== */}
      <section id="cta-section" className="py-16 sm:py-20" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
            ¿Listo para empezar?
          </h2>
          <p className="text-white/80 mb-8 text-lg">
            Solicita tu presupuesto sin compromiso. Respuesta en menos de 24 horas.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Button 
              size="lg"
              onClick={handlePresupuesto}
              className="bg-white text-slate-900 hover:bg-slate-100 px-8"
            >
              Solicitar presupuesto
              <ArrowRight size={18} className="ml-2" />
            </Button>
            <Button 
              size="lg"
              variant="outline"
              onClick={() => handleWhatsApp()}
              className="border-white/30 text-white hover:bg-white/10 px-8"
            >
              <MessageCircle size={18} className="mr-2" />
              Escribir por WhatsApp
            </Button>
          </div>
        </div>
      </section>

      {/* ========== FOOTER ========== */}
      <footer id="contacto" className="bg-slate-900 text-white py-12">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {/* Info */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                {logo ? (
                  <img src={logo} alt={name} className="h-10 w-auto" />
                ) : (
                  <div 
                    className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold"
                    style={{ backgroundColor: primaryColor }}
                  >
                    {name?.charAt(0)}
                  </div>
                )}
                <span className="font-semibold">{name}</span>
              </div>
              <p className="text-slate-400 text-sm">{description}</p>
            </div>

            {/* Contact */}
            <div>
              <h4 className="font-semibold mb-4">Contacto</h4>
              <div className="space-y-3 text-sm text-slate-400">
                {phone && (
                  <a href={`tel:${phone}`} className="flex items-center gap-2 hover:text-white transition-colors">
                    <Phone size={16} />
                    {phone}
                  </a>
                )}
                {email && (
                  <a href={`mailto:${email}`} className="flex items-center gap-2 hover:text-white transition-colors">
                    <Mail size={16} />
                    {email}
                  </a>
                )}
                <div className="flex items-center gap-2">
                  <MapPin size={16} />
                  {city}{province && `, ${province}`}
                </div>
              </div>
            </div>

            {/* Hours */}
            {business_hours && (
              <div>
                <h4 className="font-semibold mb-4">Horario</h4>
                <div className="space-y-2 text-sm text-slate-400">
                  {business_hours.monday && (
                    <div className="flex justify-between">
                      <span>Lun - Vie</span>
                      <span>{business_hours.monday.start} - {business_hours.monday.end}</span>
                    </div>
                  )}
                  {business_hours.saturday && (
                    <div className="flex justify-between">
                      <span>Sábado</span>
                      <span>{business_hours.saturday.start} - {business_hours.saturday.end}</span>
                    </div>
                  )}
                  {!business_hours.sunday && (
                    <div className="flex justify-between">
                      <span>Domingo</span>
                      <span>Cerrado</span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-slate-800 mt-8 pt-8 text-center text-sm text-slate-500">
            <p>© {new Date().getFullYear()} {name}. Todos los derechos reservados.</p>
          </div>
        </div>
      </footer>

      {/* ========== BARRA FIXA MOBILE ========== */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 safe-area-pb z-50">
        <div className="grid grid-cols-3 divide-x divide-slate-200">
          <button 
            onClick={() => handleWhatsApp()}
            className="flex flex-col items-center justify-center py-3 text-green-600 active:bg-green-50 transition-colors"
          >
            <MessageCircle size={20} />
            <span className="text-xs mt-1 font-medium">WhatsApp</span>
          </button>
          <button 
            onClick={handlePresupuesto}
            className="flex flex-col items-center justify-center py-3 active:bg-blue-50 transition-colors"
            style={{ color: primaryColor }}
          >
            <FileCheck size={20} />
            <span className="text-xs mt-1 font-medium">Presupuesto</span>
          </button>
          <button 
            onClick={handleCita}
            className="flex flex-col items-center justify-center py-3 text-slate-700 active:bg-slate-50 transition-colors"
          >
            <Calendar size={20} />
            <span className="text-xs mt-1 font-medium">Cita</span>
          </button>
        </div>
      </div>

      {/* Spacer for mobile bottom bar */}
      <div className="h-20 md:hidden" />
    </div>
  );
};

export default TemplateMadrid;
